import { kebabCase } from 'lodash-es'
import { type ReactNode, useEffect, useRef } from 'react'
import {
  Tab as AriaTab,
  TabList as AriaTabList,
  TabPanel as AriaTabPanel,
  Tabs as AriaTabs,
  type Key,
} from 'react-aria-components'
import { twMerge as cn } from 'tailwind-merge'
import IconParkOutlineShopping from '~icons/icon-park-outline/shopping'
import Text from 'src/components/ui/Text'
import { CollectionCardPaginatedList } from 'src/features/UserCollectionCard/CollectionCardList'
import { DecorationItemGroupInUserProfile } from 'src/features/UserDecoration/components/DecorationItemGroupInUserProfile'
import { DecorationType } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import { useQs } from 'src/utils/qs'
import { track, useTrackPageView } from 'src/utils/tracker'
import { useListUserDecorations } from '~/services/apollo/user-decoration'
import { MioExpression } from '../Common/MioImage'
import type { BaseCursorPagingRef } from '../CursorPaging/BaseCursorPaging'
import BaseCursorPaging from '../CursorPaging/BaseCursorPaging'
import Empty from '../Empty'
import { UILink } from '../ui/Link'
import { DecorationPageTab } from './constants'
import { useUserProfileContext } from './UserProfile.context'

const TABS: TheTabItem[] = [
  {
    value: DecorationPageTab.Badge,
    label: <Text i18nKey="common:user-badge.label" values={{ count: 2 }} />,
  },
  {
    value: DecorationPageTab.AvatarFrame,
    label: (
      <Text i18nKey="common:user-avatar-frame.label" values={{ count: 2 }} />
    ),
  },
  {
    value: DecorationPageTab.CollectionCard,
    label: (
      <Text i18nKey="common:user-collection-card.label" values={{ count: 2 }} />
    ),
  },
  {
    value: DecorationPageTab.ArtworkFrame,
    label: (
      <Text i18nKey="common:user-artwork-frame.label" values={{ count: 2 }} />
    ),
  },
  {
    value: DecorationPageTab.LiveBanner,
    label: (
      <Text i18nKey="common:user-live-banner.label" values={{ count: 2 }} />
    ),
  },
]

export default function UserDecorationsTab(props: {
  currentTab: string
  onChange: (value: string) => any
}) {
  const currTab = props.currentTab as DecorationPageTab

  const tabs = TABS

  const trackPageView = useTrackPageView()
  const trackPageViewRef = useRef(trackPageView)
  trackPageViewRef.current = trackPageView

  useEffect(() => {
    trackPageViewRef.current({ scene: 'decoration', section: currTab })
  }, [currTab])

  return (
    <div className="pb-12 space-y-4">
      <AriaTabs
        selectedKey={currTab}
        onSelectionChange={value => props.onChange(value as DecorationPageTab)}
      >
        <TheTabList items={tabs} className="mb-4" />
        <BadgePage />
        <AvatarFramePage />
        <CollectionCardsPage />
        <ArtworkFramePage />
        <LiveBannerPage />
      </AriaTabs>
    </div>
  )
}

/*
 * =============================================================================
 */

function BadgePage() {
  return (
    <AriaTabPanel id={DecorationPageTab.Badge} className="contents">
      <DecorationItemsPaginatedList decorationType={DecorationType.Badge} />
    </AriaTabPanel>
  )
}

const PAGE_SIZE = 36

function DecorationItemsPaginatedList(props: {
  decorationType: DecorationType
}) {
  const { user } = useUserProfileContext()
  const userId = user.id
  const [{ before, after }, setQs] = useQs()
  const pagingRef = useRef<BaseCursorPagingRef>(null)

  return (
    <BaseCursorPaging
      useSWR={useListUserDecorations}
      getConnection={r => r}
      pageSize={PAGE_SIZE}
      renderItem={it => {
        return (
          <DecorationItemGroupInUserProfile
            key={it.id}
            decorationRelation={it}
          />
        )
      }}
      renderContainer={({ children }) => (
        <div className="flex flex-col">
          <UILink
            variant="filled"
            href="/style"
            className="rounded-lg bg-black text-white dark:text-black dark:bg-white self-end"
            icon={<IconParkOutlineShopping className="size-4" />}
            onClick={() => {
              track('shop_enter_click', { location: 'profile-decoration' })
            }}
          >
            <Text i18nKey="common:user-decoration.cosmetic-shop.title" />
          </UILink>
          <div
            className={cx(
              'grid gap-x-4 gap-y-8 py-2',
              props.decorationType === DecorationType.ArtworkFrame
                ? 'grid-cols-[repeat(auto-fill,minmax(100px,1fr))]'
                : 'grid-cols-[repeat(auto-fill,minmax(100px,1fr))]',
            )}
          >
            {children}
          </div>
        </div>
      )}
      pagingRef={pagingRef}
      empty={
        <Empty
          description={
            <p className="flex flex-col items-center gap-2">
              <Text
                i18nKey={`user:user-decoration.${kebabCase(props.decorationType.toLowerCase())}.list-empty-placeholder`}
              />
              <UILink
                variant="filled"
                href="/style"
                className="rounded-full bg-black text-white dark:text-black dark:bg-white"
                onClick={() => {
                  track('shop_enter_click', {
                    location: 'profile-decoration-empty-placeholder',
                  })
                }}
              >
                <Text i18nKey="user:user-decoration.discover-more.label" />
              </UILink>
            </p>
          }
          mioExpression={MioExpression.Idea}
        />
      }
      extraArgs={{
        id: userId,
        type: props.decorationType,
      }}
      before={before}
      after={after}
      onCursorChange={setQs}
    />
  )
}

function AvatarFramePage() {
  return (
    <AriaTabPanel id={DecorationPageTab.AvatarFrame} className="contents">
      <DecorationItemsPaginatedList
        decorationType={DecorationType.AvatarFrame}
      />
    </AriaTabPanel>
  )
}

function ArtworkFramePage() {
  return (
    <AriaTabPanel id={DecorationPageTab.ArtworkFrame} className="contents">
      <DecorationItemsPaginatedList
        decorationType={DecorationType.ArtworkFrame}
      />
    </AriaTabPanel>
  )
}

function CollectionCardsPage() {
  return (
    <AriaTabPanel id={DecorationPageTab.CollectionCard} className="contents">
      <CollectionCardPaginatedList />
    </AriaTabPanel>
  )
}

function LiveBannerPage() {
  return (
    <AriaTabPanel id={DecorationPageTab.LiveBanner} className="contents">
      <div className="space-y-4">
        <DecorationItemsPaginatedList
          decorationType={DecorationType.LiveBanner}
        />
      </div>
    </AriaTabPanel>
  )
}

/*
 * =============================================================================
 */

type TheTabItem = {
  value: Key
  label: ReactNode
}

type TheTabListProps = StyleProps & {
  items: TheTabItem[]
}

function TheTabList({ items, className, style }: TheTabListProps) {
  return (
    <AriaTabList
      items={items}
      className={cn('flex flex-wrap gap-1', className)}
      style={style}
    >
      {item => (
        <AriaTab
          id={item.value}
          className={cn(
            'px-4 py-2 rounded-full outline-none cursor-default',
            'hover:bg-black/5 dark:hover:bg-white/10',
            'selected:bg-black/10 dark:selected:text-neutral-100 dark:selected:bg-white/20',
            'focus-visible:outline focus-visible:outline-offset-1 focus-visible:outline-blue-500',
          )}
        >
          {item.label}
        </AriaTab>
      )}
    </AriaTabList>
  )
}
